/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import { at } from 'lodash';
import { useField } from 'formik';
import {
  InputLabel, FormControl, Select, MenuItem, FormHelperText,
} from '@mui/material';
import PropTypes from 'prop-types';

/**
 * SelectField Component
 * @param props
 * @returns {Element}
 * @constructor
 */
function SelectField(props) {
  const {
    label, data, onChange, ...rest
  } = props;
  const [field, meta] = useField(props);
  const { value: selectedValue } = field;
  const [touched, error] = at(meta, 'touched', 'error');
  const isError = touched && error && true;

  function _renderHelperText() {
    if (isError) {
      return <FormHelperText>{error}</FormHelperText>;
    }
    return null;
  }

  /**
     * Execute onChange
     * @param e
     */
  const combinedOnChange = (e) => {
    if (field.onChange) {
      field.onChange(e);
    }
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <FormControl {...rest} error={isError}>
      <InputLabel>
        {label}
      </InputLabel>
      <Select
        MenuProps={{ disableScrollLock: true }}
        {...field}
        value={selectedValue || ''}
        onChange={combinedOnChange}
        aria-describedby="selectDropdown"
      >
        {data.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
      {_renderHelperText()}
    </FormControl>
  );
}

export default SelectField;

SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  data: PropTypes.array.isRequired,
  onChange: PropTypes.func,
};

SelectField.defaultProps = {
  onChange: null,
};
